import React, { useState, useEffect } from 'react';
import { formatDate } from '@/lib/dateUtils';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Lock, Save, Menu, X, Users, UserPlus, Trash2, Clock, CheckCircle, Eye, Edit2, PlayCircle, Building2, MapPin, Phone, Settings, LogOut, CreditCard, Crown, AlertTriangle, ShieldCheck, Briefcase, Calculator, TrendingUp, BookOpen, Info, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import { useTour } from '@/components/WelcomeTour';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { InviteTeamMemberDialog } from '@/lib/InviteTeamMemberDialog';
import { RoleGuideModal } from '@/lib/RoleGuideModal';
import { ROLES } from '@/lib/permissions';

// Subscription tiers for display
const PLAN_TIERS = [
  { id: 'basic', name: 'Basic', price_monthly: 29, price_annual: 290 },
  { id: 'pro', name: 'Pro', price_monthly: 69, price_annual: 690 },
  { id: 'enterprise', name: 'Enterprise', price_monthly: 199, price_annual: 1990 }
];

// Card update form component
function UpdateCardForm({ onSuccess, onCancel, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card: elements.getElement(CardElement) }
    });

    if (stripeError) {
      setError(stripeError.message);
      setIsProcessing(false);
    } else {
      onSuccess(setupIntent.payment_method);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg bg-slate-50">
        <CardElement options={{
          style: {
            base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' } },
            invalid: { color: '#ef4444' }
          }
        }} />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}
      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">Cancel</Button>
        <Button type="submit" disabled={!stripe || isProcessing} className="flex-1">
          {isProcessing ? 'Processing...' : 'Update Card'}
        </Button>
      </div>
    </form>
  );
}

function Profile({ user, onLogout, onUpdateUser }) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSyncingPayments, setIsSyncingPayments] = useState(false);
  const { startTour } = useTour();
  
  // Check if user is a team member (investor/accountant/PM)
  const isTeamMember = user?.account_type === 'team_member';
  const [userRoles, setUserRoles] = useState([]);
  const isInvestor = userRoles.includes('investor');
  
  // Fetch user's specific roles if they're a team member
  useEffect(() => {
    const fetchRoles = async () => {
      if (!isTeamMember || !user?.id) return;
      try {
        const response = await apiService.getMyTeamAccess(user.id);
        if (response.data && response.data.length > 0) {
          const access = response.data[0];
          const roles = access.roles && access.roles.length > 0 
            ? access.roles 
            : [access.role || 'investor'];
          setUserRoles(roles);
        }
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      }
    };
    fetchRoles();
  }, [isTeamMember, user?.id]);
  
  // Profile form
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Payer/Entity Info form
  const [businessName, setBusinessName] = useState(user.business_name || '');
  const [taxId, setTaxId] = useState(user.tax_id || '');
  const [stateTaxId, setStateTaxId] = useState(user.state_tax_id || '');
  const [address, setAddress] = useState(user.address || '');
  const [city, setCity] = useState(user.city || '');
  const [state, setState] = useState(user.state || '');
  const [zipCode, setZipCode] = useState(user.zip_code || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [isPayerLoading, setIsPayerLoading] = useState(false);

  // Team Management state
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [myTeamAccess, setMyTeamAccess] = useState([]);
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [isRoleGuideOpen, setIsRoleGuideOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  
  // Edit Team Member state
  const [editingMember, setEditingMember] = useState(null);
  const [isEditMemberDialogOpen, setIsEditMemberDialogOpen] = useState(false);
  const [editMemberProjects, setEditMemberProjects] = useState([]);
  const [editMemberPermissions, setEditMemberPermissions] = useState([]);

  // Feature permissions for investors
  const FEATURE_PERMISSIONS = [
    { id: 'view_dashboard', label: 'Dashboard', description: 'View assigned projects overview', locked: true },
    { id: 'view_projects', label: 'Project Details', description: 'View project progress and phases', locked: true },
    { id: 'view_reports', label: 'Reports', description: 'Access draw reports and history', locked: false },
    { id: 'view_financial_analysis', label: 'Financial Analysis', description: 'View ROI and projections', locked: false },
    { id: 'view_project_hub', label: 'Project Hub', description: 'View contractors and documents', locked: false },
    { id: 'view_bank_templates', label: 'Bank Templates', description: 'View lien waiver templates', locked: false },
  ];

  // Subscription Management state
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdateCardModal, setShowUpdateCardModal] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState('monthly');
  const [isChangingPlan, setIsChangingPlan] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [setupIntentSecret, setSetupIntentSecret] = useState(null);

  // Delete Account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Load team data and subscription on component mount
  useEffect(() => {
    loadTeamData();
    loadProjects();
    loadSubscriptionDetails();
    loadStripeKey();
  }, [user.id]);

  const loadProjects = async () => {
    try {
      const res = await apiService.getProjects(user.id);
      setProjects(res.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  };

  const loadStripeKey = async () => {
    try {
      const res = await apiService.getStripePublishableKey();
      if (res.data?.publishable_key) {
        setStripePromise(loadStripe(res.data.publishable_key));
      }
    } catch (error) {
      console.error('Failed to load Stripe:', error);
    }
  };

  const loadSubscriptionDetails = async () => {
    try {
      setIsLoadingSubscription(true);
      const res = await apiService.getSubscriptionDetails(user.id);
      setSubscriptionDetails(res.data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  const handleChangePlan = async () => {
    if (!selectedNewPlan) return;
    
    setIsChangingPlan(true);
    try {
      await apiService.changePlan(user.id, selectedNewPlan, selectedBillingCycle);
      toast.success('Plan changed successfully!');
      setShowChangePlanModal(false);
      loadSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setIsChangingPlan(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      await apiService.cancelSubscription(user.id);
      toast.success('Subscription will be cancelled at the end of your billing period');
      setShowCancelModal(false);
      loadSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await apiService.reactivateSubscription(user.id);
      toast.success('Subscription reactivated!');
      loadSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate subscription');
    }
  };

  const handleOpenUpdateCard = async () => {
    try {
      const res = await apiService.createCustomerSetupIntent(user.id);
      setSetupIntentSecret(res.data.client_secret);
      setShowUpdateCardModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize card update');
    }
  };

  const handleCardUpdateSuccess = async (paymentMethodId) => {
    try {
      await apiService.updatePaymentMethod(user.id, paymentMethodId);
      toast.success('Payment method updated!');
      setShowUpdateCardModal(false);
      loadSubscriptionDetails();
    } catch (error) {
      toast.error('Failed to save new payment method');
    }
  };

  const loadTeamData = async () => {
    try {
      const [membersRes, invitesRes, accessRes] = await Promise.all([
        apiService.getTeamMembers(user.id),
        apiService.getPendingInvites(user.id),
        apiService.getMyTeamAccess(user.id)
      ]);
      setTeamMembers(membersRes.data);
      setPendingInvites(invitesRes.data);
      setMyTeamAccess(accessRes.data);
    } catch (error) {
      console.error('Error loading team data:', error);
    }
  };
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const updates = {};
      if (name !== user.name) updates.name = name;
      if (email !== user.email) updates.email = email;

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setIsLoading(false);
        return;
      }

      const response = await apiService.updateProfile(user.id, updates);
      
      // Update user in localStorage and parent state
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (onUpdateUser) {
        onUpdateUser(updatedUser);
      }
      
      toast.success('Profile updated successfully!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Email already in use');
      } else {
        toast.error('Failed to update profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePayerInfo = async (e) => {
    e.preventDefault();
    setIsPayerLoading(true);

    try {
      const updates = {
        business_name: businessName || null,
        tax_id: taxId || null,
        state_tax_id: stateTaxId || null,
        address: address || null,
        city: city || null,
        state: state || null,
        zip_code: zipCode || null,
        phone: phone || null
      };

      const response = await apiService.updateProfile(user.id, updates);
      
      const updatedUser = response.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (onUpdateUser) {
        onUpdateUser(updatedUser);
      }
      
      toast.success('Payer information saved!');
    } catch (error) {
      toast.error('Failed to save payer information');
    } finally {
      setIsPayerLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      await apiService.changePassword(user.id, {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Current password is incorrect');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Team Management handlers
  const handleInviteTeamMember = async (inviteData) => {
    setIsInviting(true);
    try {
      await apiService.inviteTeamMember(user.id, inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      loadTeamData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Failed to send invitation');
      } else {
        toast.error('Failed to send invitation');
      }
      throw error; // Re-throw to prevent dialog from closing
    } finally {
      setIsInviting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }

    setIsDeleting(true);
    try {
      await apiService.deleteAccount(user.id, deletePassword, deleteConfirmation);
      toast.success('Account deleted successfully');
      onLogout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this invitation?')) return;
    try {
      await apiService.cancelInvite(inviteId, user.id);
      toast.success('Invitation cancelled');
      loadTeamData();
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleRemoveTeamMember = async (memberId, memberEmail) => {
    if (!window.confirm(`Remove ${memberEmail} from your team?`)) return;
    try {
      await apiService.removeTeamMember(memberId, user.id);
      toast.success('Team member removed');
      loadTeamData();
    } catch (error) {
      toast.error('Failed to remove team member');
    }
  };

  const handleEditTeamMember = (member) => {
    setEditingMember(member);
    setEditMemberProjects(member.assigned_projects || []);
    setEditMemberPermissions(member.custom_permissions || ['view_dashboard', 'view_projects']);
    setIsEditMemberDialogOpen(true);
  };

  const handleSaveTeamMember = async () => {
    if (!editingMember) return;
    
    try {
      const updateData = {
        assigned_projects: editMemberProjects
      };
      
      // Include permissions for investors
      if ((editingMember.roles || []).includes('investor')) {
        updateData.custom_permissions = editMemberPermissions;
      }
      
      await apiService.updateTeamMember(editingMember.id, user.id, updateData);
      toast.success('Team member updated');
      setIsEditMemberDialogOpen(false);
      setEditingMember(null);
      loadTeamData();
    } catch (error) {
      toast.error('Failed to update team member');
    }
  };

  const toggleEditMemberProject = (projectId) => {
    setEditMemberProjects(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const toggleEditMemberPermission = (permissionId) => {
    const perm = FEATURE_PERMISSIONS.find(p => p.id === permissionId);
    if (perm?.locked) return;
    
    setEditMemberPermissions(prev => 
      prev.includes(permissionId) 
        ? prev.filter(id => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  // Sync supplier payment data for all phases
  const handleSyncPayments = async () => {
    setIsSyncingPayments(true);
    try {
      const res = await apiService.syncSupplierPayments();
      const count = res.data?.updated_count || 0;
      if (count > 0) {
        toast.success(`Successfully synced payment data for ${count} phases. Refresh your projects to see updated amounts.`);
      } else {
        toast.info('All payment data is already in sync!');
      }
    } catch (err) {
      console.error('Sync error:', err);
      toast.error('Failed to sync payment data. Please try again.');
    } finally {
      setIsSyncingPayments(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'accountant':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'assistant':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'accountant':
        return 'View-only access to projects and reports';
      case 'assistant':
        return 'Can view projects and add expenses';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold">Account Settings</h1>
              <p className="text-blue-100 mt-1 text-xs sm:text-sm hidden sm:block">Manage your profile and security</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile Information
            </CardTitle>
            <CardDescription>
              Update your account details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Payer/Entity Information - Hide for investors */}
        {!isInvestor && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Payer / Entity Information
            </CardTitle>
            <CardDescription>
              Business details used for 1099 forms and tax documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdatePayerInfo} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="business-name">Business / Entity Name</Label>
                <Input
                  id="business-name"
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Your Company LLC"
                  className="h-11"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tax-id">Federal Tax ID (EIN)</Label>
                  <Input
                    id="tax-id"
                    type="text"
                    value={taxId}
                    onChange={(e) => setTaxId(e.target.value)}
                    placeholder="XX-XXXXXXX"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">PAYER'S TIN on 1099</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state-tax-id">State Payer's ID #</Label>
                  <Input
                    id="state-tax-id"
                    type="text"
                    value={stateTaxId}
                    onChange={(e) => setStateTaxId(e.target.value)}
                    placeholder="State ID number"
                    className="h-11"
                  />
                  <p className="text-xs text-slate-500">Box 6 on 1099-NEC</p>
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="address" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Street Address
                </Label>
                <Input
                  id="address"
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="123 Main Street"
                  className="h-11"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Cedar Rapids"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="IA"
                    maxLength={2}
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    type="text"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    placeholder="52402"
                    className="h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                disabled={isPayerLoading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {isPayerLoading ? 'Saving...' : 'Save Payer Info'}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-11"
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="h-11"
                />
                <p className="text-xs text-slate-500">Must be at least 6 characters</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="h-11"
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                {isLoading ? 'Updating...' : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Team Management - Only show for subscribers (account owners) */}
        {user?.account_type !== 'team_member' && (
          <>
            <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Management
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsRoleGuideOpen(true)}
                  className="text-slate-600"
                  data-testid="role-guide-btn"
                >
                  <BookOpen className="w-4 h-4 mr-1" />
                  Role Guide
                </Button>
                <Button
                  onClick={() => setIsInviteDialogOpen(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="invite-team-member-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                Invite Member
              </Button>
              </div>
            </CardTitle>
            <CardDescription>
              Invite project managers, accountants, or investors to help manage your projects
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Team Member Limit Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
              <div className="text-sm">
                <span className="text-slate-600">Team Members: </span>
                <span className="font-semibold text-blue-700">
                  {teamMembers.length + pendingInvites.length}
                </span>
                <span className="text-slate-500"> / </span>
                <span className="font-medium text-slate-600">
                  {user?.subscription_tier === 'enterprise' ? '∞' : 
                   user?.subscription_tier === 'pro' || user?.subscription_tier === 'trial' ? '3' : '1'}
                </span>
                <span className="text-slate-500 ml-2 text-xs">
                  ({user?.subscription_tier === 'basic' ? 'Basic plan' : 
                    user?.subscription_tier === 'pro' ? 'Pro plan' : 
                    user?.subscription_tier === 'enterprise' ? 'Enterprise plan' : 'Trial'})
                </span>
              </div>
              {user?.subscription_tier === 'basic' && (
                <span className="text-xs text-blue-600 font-medium">Upgrade to Pro for 3 members</span>
              )}
            </div>
            
            {/* Team Members */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-3">Team Members</h4>
              {teamMembers.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg">
                  <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 text-sm">No team members yet</p>
                  <p className="text-slate-400 text-xs mt-1">Invite project managers, accountants, or investors</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamMembers.map((member) => {
                    const memberRoles = member.roles || [member.role];
                    const assignedProjects = member.assigned_projects || [];
                    
                    return (
                      <div key={member.id} className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{member.name || member.user_email || member.email}</p>
                            <p className="text-xs text-slate-500">{member.user_email || member.email}</p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                              {memberRoles.map(role => {
                                const roleInfo = ROLES[role];
                                if (!roleInfo) return null;
                                return (
                                  <Badge 
                                    key={role} 
                                    variant="outline" 
                                    className={`${roleInfo.color.badge} text-xs`}
                                  >
                                    {role === 'project_manager' && <Briefcase className="w-3 h-3 mr-1" />}
                                    {role === 'accountant' && <Calculator className="w-3 h-3 mr-1" />}
                                    {role === 'investor' && <TrendingUp className="w-3 h-3 mr-1" />}
                                    {roleInfo.name}
                                  </Badge>
                                );
                              })}
                            </div>
                            {memberRoles.includes('project_manager') && assignedProjects.length > 0 && (
                              <div className="mt-2 text-xs text-slate-500">
                                <span className="font-medium">Projects:</span>{' '}
                                {assignedProjects.length} assigned
                              </div>
                            )}
                            {memberRoles.includes('investor') && (
                              <div className="mt-2 text-xs text-slate-500">
                                <span className="font-medium">Investment Projects:</span>{' '}
                                {assignedProjects.length > 0 ? `${assignedProjects.length} assigned` : <span className="text-amber-600">None assigned (investor cannot see any projects)</span>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTeamMember(member)}
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTeamMember(member.id, member.user_email || member.email)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Pending Invitations</h4>
                <div className="space-y-3">
                  {pendingInvites.map((invite) => {
                    const inviteRoles = invite.roles || [invite.role];
                    
                    return (
                      <div key={invite.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                            <Clock className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{invite.email}</p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {inviteRoles.map(role => {
                                const roleInfo = ROLES[role];
                                if (!roleInfo) return null;
                                return (
                                  <Badge 
                                    key={role} 
                                    variant="outline" 
                                    className={`${roleInfo.color.badge} text-xs`}
                                  >
                                    {roleInfo.name}
                                  </Badge>
                                );
                              })}
                              <span className="text-xs text-amber-600">• Pending</span>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvite(invite.id)}
                          className="text-amber-600 hover:text-amber-700 hover:bg-amber-100"
                        >
                          Cancel
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* My Team Access (if user is part of other teams) */}
            {myTeamAccess.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Teams I Belong To</h4>
                <div className="space-y-3">
                  {myTeamAccess.map((access) => (
                    <div key={access.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{access.owner_name || 'Project Owner'}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className={getRoleBadgeColor(access.role)}>
                              {access.role === 'accountant' ? 'Accountant' : 'Assistant'}
                            </Badge>
                            <span className="text-xs text-green-600">Active</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invite Team Member Dialog - New Component */}
        <InviteTeamMemberDialog
          open={isInviteDialogOpen}
          onOpenChange={setIsInviteDialogOpen}
          projects={projects}
          onInvite={handleInviteTeamMember}
          isLoading={isInviting}
        />

        {/* Role Guide Modal */}
        <RoleGuideModal
          open={isRoleGuideOpen}
          onOpenChange={setIsRoleGuideOpen}
        />

        {/* Edit Team Member Dialog */}
        <Dialog open={isEditMemberDialogOpen} onOpenChange={setIsEditMemberDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Team Member</DialogTitle>
              <DialogDescription>
                Update project assignments for {editingMember?.name || editingMember?.email}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Member Info */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Email:</span> {editingMember?.user_email || editingMember?.email}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Role:</span> {(editingMember?.roles || [editingMember?.role]).map(r => 
                    r === 'investor' ? 'Investor' : 
                    r === 'project_manager' ? 'Project Manager' : 
                    r === 'accountant' ? 'Accountant' : r
                  ).join(', ')}
                </p>
              </div>

              {/* Project Assignment */}
              <div>
                <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  Assigned Projects
                </Label>
                
                {(editingMember?.roles || []).includes('investor') && editMemberProjects.length === 0 && (
                  <p className="text-xs text-amber-600 mb-2 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    Investor has no projects assigned and cannot see any data
                  </p>
                )}
                
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  {projects.length === 0 ? (
                    <p className="text-sm text-slate-500 p-3">No projects available</p>
                  ) : (
                    projects.map(project => (
                      <label
                        key={project.id}
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={editMemberProjects.includes(project.id)}
                          onChange={() => toggleEditMemberProject(project.id)}
                          className="w-4 h-4 rounded border-slate-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{project.name}</p>
                          <p className="text-xs text-slate-500 truncate">{project.address}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Feature Permissions (for investors only) */}
              {(editingMember?.roles || []).includes('investor') && (
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                    <Eye className="w-4 h-4 text-emerald-600" />
                    Feature Access
                  </Label>
                  <p className="text-xs text-slate-500 mb-2">Toggle which features this investor can access</p>
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {FEATURE_PERMISSIONS.map(perm => (
                      <label
                        key={perm.id}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                          perm.locked ? 'bg-slate-50 cursor-not-allowed' : 
                          editMemberPermissions.includes(perm.id) ? 'bg-emerald-50 border-emerald-200' : 'hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={editMemberPermissions.includes(perm.id)}
                          onChange={() => toggleEditMemberPermission(perm.id)}
                          disabled={perm.locked}
                          className="w-4 h-4 rounded border-slate-300 text-emerald-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">
                            {perm.label}
                            {perm.locked && <span className="text-xs text-slate-400 ml-1">(Required)</span>}
                          </p>
                          <p className="text-xs text-slate-500">{perm.description}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setIsEditMemberDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveTeamMember}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* Take a Tour */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlayCircle className="w-5 h-5" />
              Welcome Tour
            </CardTitle>
            <CardDescription>
              Take a guided tour of DrawReady&apos;s features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => {
                navigate('/dashboard');
                setTimeout(() => startTour(), 500);
              }}
              variant="outline"
              className="w-full h-11"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Take the Tour Again
            </Button>
          </CardContent>
        </Card>

        {/* Subscription Management - Only show for subscribers (account owners) */}
        {user?.account_type !== 'team_member' && (
          <>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                  Subscription
                </CardTitle>
            <CardDescription>
              Manage your plan, billing, and payment method
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingSubscription ? (
              <div className="text-center py-4">
                <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : subscriptionDetails?.has_subscription ? (
              <>
                {/* Current Plan */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Crown className="w-5 h-5 text-yellow-500" />
                      <span className="font-semibold text-lg">
                        {subscriptionDetails.tier?.name || subscriptionDetails.tier_id} Plan
                      </span>
                    </div>
                    <Badge className={
                      subscriptionDetails.status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                      subscriptionDetails.status === 'active' ? 'bg-green-100 text-green-700' :
                      subscriptionDetails.cancel_at_period_end ? 'bg-yellow-100 text-yellow-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {subscriptionDetails.cancel_at_period_end ? 'Cancelling' :
                       subscriptionDetails.status === 'trialing' ? 'Trial' :
                       subscriptionDetails.status}
                    </Badge>
                  </div>
                  
                  <div className="text-sm text-slate-600 space-y-1">
                    <p>
                      <span className="font-medium">${subscriptionDetails.tier?.monthly_price || '—'}</span>/month
                      {subscriptionDetails.billing_cycle === 'annual' && ' (billed annually)'}
                    </p>
                    {subscriptionDetails.status === 'trialing' && subscriptionDetails.trial_ends_at && (
                      <p className="text-blue-600">
                        Trial ends: {formatDate(subscriptionDetails.trial_ends_at)}
                      </p>
                    )}
                    {subscriptionDetails.current_period_end && (
                      <p>
                        {subscriptionDetails.cancel_at_period_end ? 'Access until: ' : 'Next billing: '}
                        {formatDate(subscriptionDetails.current_period_end)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Payment Method */}
                {subscriptionDetails.payment_method && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-6 bg-gradient-to-r from-slate-700 to-slate-900 rounded flex items-center justify-center text-white text-xs font-bold">
                        {subscriptionDetails.payment_method.brand?.toUpperCase()?.slice(0, 4)}
                      </div>
                      <span className="text-sm">
                        •••• •••• •••• {subscriptionDetails.payment_method.last4}
                      </span>
                      <span className="text-xs text-slate-500">
                        Exp {subscriptionDetails.payment_method.exp_month}/{subscriptionDetails.payment_method.exp_year}
                      </span>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleOpenUpdateCard}>
                      Update
                    </Button>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setSelectedNewPlan(subscriptionDetails.tier_id);
                      setSelectedBillingCycle(subscriptionDetails.billing_cycle || 'monthly');
                      setShowChangePlanModal(true);
                    }}
                  >
                    Change Plan
                  </Button>
                  {subscriptionDetails.cancel_at_period_end ? (
                    <Button 
                      variant="outline"
                      className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                      onClick={handleReactivateSubscription}
                    >
                      Reactivate
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={() => setShowCancelModal(true)}
                    >
                      Cancel Plan
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="text-center py-2">
                  <p className="text-slate-500">No active subscription</p>
                </div>
                
                {/* Plan Selection */}
                <div className="space-y-2">
                  {PLAN_TIERS.map((plan) => (
                    <div
                      key={plan.id}
                      onClick={() => setSelectedNewPlan(plan.id)}
                      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedNewPlan === plan.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{plan.name}</span>
                        <span className="font-semibold">${plan.price_monthly}<span className="text-sm font-normal text-slate-500">/mo</span></span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <Button 
                  className="w-full"
                  onClick={() => navigate('/subscribe')}
                >
                  Start Free Trial
                </Button>
                <p className="text-xs text-center text-slate-500">30-day free trial, cancel anytime</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Plan Modal */}
        <Dialog open={showChangePlanModal} onOpenChange={setShowChangePlanModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Change Your Plan</DialogTitle>
              <DialogDescription>
                Select a new plan. Changes take effect immediately.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              {/* Billing Toggle */}
              <div className="flex justify-center">
                <div className="bg-slate-100 rounded-full p-1 flex">
                  <button 
                    onClick={() => setSelectedBillingCycle('monthly')} 
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedBillingCycle === 'monthly' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
                  >
                    Monthly
                  </button>
                  <button 
                    onClick={() => setSelectedBillingCycle('annual')} 
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedBillingCycle === 'annual' ? 'bg-white shadow text-slate-900' : 'text-slate-600'}`}
                  >
                    Annual <span className="text-green-600 text-xs">Save 17%</span>
                  </button>
                </div>
              </div>

              {/* Plan Options */}
              <div className="space-y-2">
                {PLAN_TIERS.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedNewPlan(plan.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedNewPlan === plan.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 hover:border-slate-300'
                    } ${subscriptionDetails?.tier_id === plan.id ? 'ring-2 ring-green-200' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{plan.name}</span>
                        {subscriptionDetails?.tier_id === plan.id && (
                          <Badge className="ml-2 bg-green-100 text-green-700">Current</Badge>
                        )}
                      </div>
                      <span className="font-semibold">
                        ${selectedBillingCycle === 'annual' ? plan.price_annual : plan.price_monthly}
                        <span className="text-sm font-normal text-slate-500">
                          /{selectedBillingCycle === 'annual' ? 'yr' : 'mo'}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChangePlanModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleChangePlan} 
                disabled={isChangingPlan || selectedNewPlan === subscriptionDetails?.tier_id}
              >
                {isChangingPlan ? 'Changing...' : 'Confirm Change'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Cancel Subscription
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel your subscription?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  Your subscription will remain active until the end of your current billing period. 
                  After that, you'll lose access to premium features.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancelModal(false)}>
                Keep My Plan
              </Button>
              <Button 
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                className="bg-red-600 hover:bg-red-700"
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Card Modal */}
        <Dialog open={showUpdateCardModal} onOpenChange={setShowUpdateCardModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Update Payment Method</DialogTitle>
              <DialogDescription>
                Enter your new card details below
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {stripePromise && setupIntentSecret ? (
                <Elements stripe={stripePromise}>
                  <UpdateCardForm
                    clientSecret={setupIntentSecret}
                    onSuccess={handleCardUpdateSuccess}
                    onCancel={() => setShowUpdateCardModal(false)}
                  />
                </Elements>
              ) : (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
          </>
        )}

        {/* Data Tools - Sync Payment Data */}
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-600">
              <Settings className="w-5 h-5" />
              Data Tools
            </CardTitle>
            <CardDescription>
              Tools to maintain and sync your project data
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">Sync Payment Data</h4>
              <p className="text-sm text-blue-700 mb-3">
                If your labor phases are showing incorrect "paid" amounts, use this tool to recalculate and sync the payment totals from your actual payment records.
              </p>
              <Button
                onClick={handleSyncPayments}
                disabled={isSyncingPayments}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSyncingPayments ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Syncing...
                  </>
                ) : (
                  <>
                    <Settings className="w-4 h-4 mr-2" />
                    Sync Payment Data
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delete Account */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Account
            </CardTitle>
            <CardDescription>
              Permanently delete your account and all associated data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This action cannot be undone. All your projects, contractors, bid requests, and other data will be permanently deleted.
              </p>
            </div>
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="w-full h-11 border-red-300 text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete My Account
            </Button>
          </CardContent>
        </Card>

        {/* Delete Account Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete Account
              </DialogTitle>
              <DialogDescription>
                This action is permanent and cannot be undone
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700 font-medium mb-2">The following will be permanently deleted:</p>
                <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                  <li>All your projects and phases</li>
                  <li>All contractors and bid requests</li>
                  <li>All documents and receipts</li>
                  <li>Your subscription will be cancelled</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-password">Enter your password</Label>
                <Input
                  id="delete-password"
                  type="password"
                  placeholder="Your password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="delete-confirm">Type <strong>DELETE</strong> to confirm</Label>
                <Input
                  id="delete-confirm"
                  type="text"
                  placeholder="DELETE"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="h-11"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeletePassword('');
                  setDeleteConfirmation('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={isDeleting || deleteConfirmation !== 'DELETE' || !deletePassword}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Logout */}
        <Card>
          <CardHeader>
            <CardTitle>Logout</CardTitle>
            <CardDescription>
              Sign out of your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={onLogout}
              variant="outline"
              className="w-full h-11"
            >
              Logout
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Profile;
